import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Chip, Tabs, Tab, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Snackbar,
  FormControlLabel, Switch, Tooltip, IconButton,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Logout as SignOutIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  PauseCircle as SuspendIcon,
  PlayCircle as ActivateIcon,
  PersonRemove as RemoveIcon,
  AdminPanelSettings as AdminIcon,
  KeyOff as KeyOffIcon,
  PhonelinkLock as AuthenticatorIcon,
} from "@mui/icons-material";

// Platform Console: operators who look after every tenant. The server only answers for users
// in nexsoldb.platform_admin (see server-postgres docs/workflows/PLATFORM-CONSOLE-RUNBOOK.md).

const BASE = "/api/platform";
const MFA_EVENT = "platform-mfa-required";

async function api(path, { method = "GET", body } = {}) {
  const token = localStorage.getItem("jwtToken");
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (res.status === 403 && data?.code === "mfa-required") {
    // The server wants an authenticator code on this sign-in: send the page back to the code screen.
    window.dispatchEvent(new Event(MFA_EVENT));
    throw new Error(data.message);
  }
  if (res.status === 403) throw new Error("Only platform admins can use this page.");
  if (!res.ok || data?.success === false) throw new Error(data?.message || data?.error || `Request failed (${res.status})`);
  return data;
}

function ago(ts) {
  if (!ts) return "never";
  const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

function when(ts) {
  return ts ? new Date(ts).toLocaleString() : "";
}

function device(ua, clientType) {
  if (!ua) return clientType === "WEB" ? "Browser" : "App";
  if (ua.includes("Electron/")) return "POS app";
  if (ua.startsWith("TradeLink247-Weighbridge")) return "Weighbridge app";
  if (ua === "Mozilla/5.0") return "Desktop app";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari" : clientType === "WEB" ? "Browser" : "App";
  const os = /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows"
    : /Mac OS/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}

function company(row) {
  return row.companyName && row.companyName !== row.tenantId ? row.companyName : "";
}

function Stat({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: "1 1 140px", minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: color || "text.primary" }}>{value ?? "–"}</Typography>
    </Paper>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={{ width: { xs: "100%", sm: 320 } }}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
    />
  );
}

const cellSx = { whiteSpace: "nowrap" };

// The console is mounted only after the server confirms this sign-in is a platform admin, so a
// company admin who types /platform sees no actions at all. The server checks every call too.
// Platform admins then need a code from an authenticator app on every sign-in (server-enforced).
export default function PlatformConsolePage() {
  const [allowed, setAllowed] = useState(null); // null = checking
  const [mfa, setMfa] = useState({ enrolled: false, verified: false });

  const check = useCallback(() => {
    let live = true;
    api("/me")
      .then((d) => {
        const ok = d?.platformAdmin === true;
        localStorage.setItem("platformAdmin", ok ? "true" : "false");
        if (!live) return;
        setMfa({ enrolled: d?.mfaEnrolled === true, verified: d?.mfaVerified === true });
        setAllowed(ok);
      })
      .catch(() => { if (live) setAllowed(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => check(), [check]);

  useEffect(() => {
    const onRequired = () => setMfa((m) => ({ ...m, verified: false }));
    window.addEventListener(MFA_EVENT, onRequired);
    return () => window.removeEventListener(MFA_EVENT, onRequired);
  }, []);

  if (allowed === null) {
    return <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>;
  }
  if (!allowed) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 640 }}>
        <Alert severity="error">
          The Platform Console is only for TradeLink247 platform administrators. Company admins cannot open it.
        </Alert>
      </Box>
    );
  }
  if (!mfa.enrolled) return <AuthenticatorSetup onDone={() => setMfa({ enrolled: true, verified: true })} />;
  if (!mfa.verified) return <AuthenticatorCode onDone={() => setMfa({ enrolled: true, verified: true })} />;
  return <PlatformConsole />;
}

function CodeField({ value, onChange, onSubmit }) {
  return (
    <TextField
      autoFocus
      size="small"
      label="6-digit code"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      onKeyDown={(e) => e.key === "Enter" && value.length === 6 && onSubmit()}
      inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", style: { letterSpacing: 6, fontSize: 20 } }}
      sx={{ width: 200 }}
    />
  );
}

function AuthenticatorFrame({ title, children }) {
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 520 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AuthenticatorIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Box>
        {children}
      </Paper>
    </Box>
  );
}

function AuthenticatorSetup({ onDone }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const started = useRef(false); // each enroll call makes a new secret, so ask only once

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api("/mfa/enroll", { method: "POST" }).then(setSetup).catch((e) => setError(e.message));
  }, []);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/mfa/confirm", { method: "POST", body: { code } });
      onDone();
    } catch (e) {
      setError(e.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthenticatorFrame title="Set up your authenticator app">
      <Typography sx={{ fontSize: 14, mb: 2 }}>
        Platform actions need a code from an authenticator app (Google Authenticator, Microsoft Authenticator,
        Authy or similar). Scan this QR code with the app, then type the 6-digit code it shows.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!setup && !error && <CircularProgress size={24} />}
      {setup && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", p: 2, bgcolor: "#fff", borderRadius: 1, mb: 1.5 }}>
            <QRCodeSVG value={setup.otpauthUri} size={200} />
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2, wordBreak: "break-all" }}>
            Can't scan it? Enter this key in the app instead: <b>{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</b>
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <CodeField value={code} onChange={setCode} onSubmit={confirm} />
            <Button variant="contained" onClick={confirm} disabled={busy || code.length !== 6}>Turn on</Button>
          </Box>
        </>
      )}
    </AuthenticatorFrame>
  );
}

function AuthenticatorCode({ onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("/mfa/verify", { method: "POST", body: { code } });
      onDone();
    } catch (e) {
      setError(e.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthenticatorFrame title="Enter your authenticator code">
      <Typography sx={{ fontSize: 14, mb: 2 }}>
        Open your authenticator app and type the current 6-digit code for TradeLink247 Platform.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <CodeField value={code} onChange={setCode} onSubmit={verify} />
        <Button variant="contained" onClick={verify} disabled={busy || code.length !== 6}>Continue</Button>
      </Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 2 }}>
        Lost your phone? Ask another platform admin to reset your authenticator.
      </Typography>
    </AuthenticatorFrame>
  );
}

function PlatformConsole() {
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [audit, setAudit] = useState([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [filter, setFilter] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, text, action, danger, needsReason }
  const [reason, setReason] = useState("");
  const [newAdmin, setNewAdmin] = useState("");
  const [typed, setTyped] = useState(""); // confirmation phrase / pasted token in the dialog
  const me = useMemo(() => {
    try {
      const p = JSON.parse(atob(localStorage.getItem("jwtToken").split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      return { username: p.sub, tenantId: p.activeTenant || p.tenant };
    } catch { return {}; }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, s, u, t, a, au] = await Promise.all([
        api("/overview"),
        api(`/sessions?includeClosed=${includeClosed}`),
        api("/users"),
        api("/tenants"),
        api("/admins"),
        api("/audit?limit=200"),
      ]);
      setOverview(o); setSessions(s); setUsers(u); setTenants(t); setAdmins(a); setAudit(au);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [includeClosed]);

  useEffect(() => { load(); }, [load]);

  const ask = (c) => { setReason(""); setTyped(""); setConfirm(c); };

  const runConfirmed = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      const msg = await c.action(reason.trim(), typed.trim());
      setToast(msg);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const match = (row, keys) => {
    const f = filter.trim().toLowerCase();
    const okText = !f || keys.some((k) => String(row[k] ?? "").toLowerCase().includes(f));
    const okTenant = !tenantFilter || row.tenantId === tenantFilter;
    return okText && okTenant;
  };

  const shownSessions = sessions.filter((r) => match(r, ["username", "tenantId", "companyName", "ipAddress"]));
  const shownUsers = users.filter((r) => match(r, ["username", "email", "phone", "tenantId", "companyName"]));
  const shownTenants = tenants.filter((r) => match(r, ["tenantId", "companyName", "phone", "email"]));

  // ── Actions ────────────────────────────────────────────────────────────
  const signOutSession = (s) => ask({
    title: `Sign out ${s.username}?`,
    text: `Ends this ${device(s.userAgent, s.clientType)} sign-in. They can sign in again.`,
    needsReason: true,
    action: async (r) => { await api(`/sessions/${s.id}/sign-out`, { method: "POST", body: { reason: r } }); return `${s.username} signed out`; },
  });

  const signOutUser = (username) => ask({
    title: `Sign out ${username} everywhere?`,
    text: "Ends every token this user holds, in browsers and in POS or weighbridge apps, including old app sign-ins that are not listed here. They can sign in again.",
    needsReason: true,
    action: async (r) => {
      const d = await api("/users/sign-out", { method: "POST", body: { username, reason: r } });
      return `${username}: ${d.sessionsEnded} sign-in(s) ended`;
    },
  });

  const signOutTenant = (t) => ask({
    title: `Sign out everyone in ${company(t) || t.tenantId}?`,
    text: "Ends every token of this company's users, including POS and weighbridge apps. They can sign in again; the subscription is not changed.",
    needsReason: true,
    action: async (r) => {
      const d = await api(`/tenants/${encodeURIComponent(t.tenantId)}/sign-out`, { method: "POST", body: { reason: r } });
      return `${t.tenantId}: everyone signed out (${d.sessionsEnded} listed sign-in(s))`;
    },
  });

  const revokeToken = () => ask({
    title: "Revoke a token",
    text: "Paste the token (with or without \"Bearer\"). A token from before sign-ins were tracked also ends that user's older tokens; newer ones keep working.",
    input: { label: "Token", multiline: true },
    needsReason: true,
    action: async (r, token) => {
      const d = await api("/tokens/revoke", { method: "POST", body: { token, reason: r } });
      return d.scope === "already-expired" ? `That token of ${d.username} had already expired` : `Token of ${d.username} revoked`;
    },
  });

  const signOutEveryone = () => ask({
    title: "Sign out everyone?",
    text: "Ends every token on the platform: all users of all companies, POS and weighbridge apps, and you. Everyone has to sign in again. Type SIGN OUT EVERYONE to confirm.",
    input: { label: "Type SIGN OUT EVERYONE", match: "SIGN OUT EVERYONE" },
    danger: true,
    needsReason: true,
    action: async (r) => {
      await api("/sign-out-everyone", { method: "POST", body: { confirm: "SIGN OUT EVERYONE", reason: r } });
      return "Everyone signed out";
    },
  });

  const toggleUser = (u) => ask(u.disabled ? {
    title: `Enable ${u.username}?`,
    text: "They will be able to sign in again.",
    action: async (r) => { await api(`/users/${encodeURIComponent(u.id)}/enable`, { method: "POST", body: { reason: r } }); return `${u.username} enabled`; },
  } : {
    title: `Disable ${u.username}?`,
    text: "Signs them out everywhere and blocks sign-in. Their data is kept and you can enable them again.",
    danger: true,
    needsReason: true,
    action: async (r) => {
      const d = await api(`/users/${encodeURIComponent(u.id)}/disable`, { method: "POST", body: { reason: r } });
      return `${u.username} disabled, ${d.sessionsEnded} sign-in(s) ended`;
    },
  });

  const toggleTenant = (t) => ask(t.status === "SUSPENDED" ? {
    title: `Activate ${company(t) || t.tenantId}?`,
    text: "Users of this company can sign in again.",
    action: async (r) => { await api(`/tenants/${encodeURIComponent(t.tenantId)}/activate`, { method: "POST", body: { reason: r } }); return `${t.tenantId} activated`; },
  } : {
    title: `Suspend ${company(t) || t.tenantId}?`,
    text: `Signs out all ${t.users} user(s) of this company, including POS and weighbridge apps, and blocks sign-in until you activate it. No data is deleted.`,
    danger: true,
    needsReason: true,
    action: async (r) => {
      const d = await api(`/tenants/${encodeURIComponent(t.tenantId)}/suspend`, { method: "POST", body: { reason: r } });
      return `${t.tenantId} suspended, ${d.sessionsEnded} sign-in(s) ended`;
    },
  });

  const addAdmin = async () => {
    if (!newAdmin.trim()) return;
    try {
      await api("/admins", { method: "POST", body: { username: newAdmin.trim() } });
      setToast(`${newAdmin.trim()} is now a platform admin`);
      setNewAdmin("");
      load();
    } catch (e) { setError(e.message); }
  };

  const removeAdmin = (username) => ask({
    title: `Remove ${username} as platform admin?`,
    text: "They keep their normal account but lose this console.",
    action: async () => { await api(`/admins/${encodeURIComponent(username)}`, { method: "DELETE" }); return `${username} removed`; },
  });

  const resetAuthenticator = (username) => ask({
    title: `Reset ${username}'s authenticator?`,
    text: "Use this when they lost their phone. They set up a new authenticator the next time they open this console.",
    action: async () => { await api(`/admins/${encodeURIComponent(username)}/mfa`, { method: "DELETE" }); return `${username}'s authenticator reset`; },
  });

  const viewTenant = (tenantId, toTab) => { setTenantFilter(tenantId); setFilter(""); setTab(toTab); };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, width: "100%", maxWidth: 1400, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <AdminIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>Platform Console</Typography>
        <Button startIcon={<KeyOffIcon />} onClick={revokeToken}>Revoke a token</Button>
        <Button color="error" startIcon={<SignOutIcon />} onClick={signOutEveryone}>Sign out everyone</Button>
        <Button startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <Stat label="Companies" value={overview?.tenants} />
        <Stat label="Users" value={overview?.users} />
        <Stat label="Signed in now" value={overview?.openSessions} />
        <Stat label="Active last 15 min" value={overview?.activeLast15Min} color="success.main" />
        <Stat label="Suspended companies" value={overview?.suspendedTenants} color={overview?.suspendedTenants ? "warning.main" : undefined} />
        <Stat label="Disabled users" value={overview?.disabledUsers} color={overview?.disabledUsers ? "warning.main" : undefined} />
      </Box>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Signed in" />
          <Tab label="Users" />
          <Tab label="Companies" />
          <Tab label="Admins" />
          <Tab label="Audit" />
        </Tabs>

        {tab <= 2 && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", p: 1.5 }}>
            <SearchBox value={filter} onChange={setFilter} placeholder="Search name, phone, company…" />
            {tenantFilter && (
              <Chip label={`Company: ${tenantFilter}`} onDelete={() => setTenantFilter("")} />
            )}
            {tab === 0 && (
              <FormControlLabel
                control={<Switch size="small" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />}
                label="Show ended sign-ins"
              />
            )}
            {tab === 0 && overview && (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Browsers sign out after {Math.round(overview.webIdleMinutes / 60)} h idle. Apps stay signed in until signed out here.
              </Typography>
            )}
          </Box>
        )}

        {tab === 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell>Signed in</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {shownSessions.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={cellSx}>{s.username}</TableCell>
                    <TableCell sx={cellSx}>{company(s) || s.tenantId}{company(s) && <Typography component="span" sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>{s.tenantId}</Typography>}</TableCell>
                    <TableCell sx={cellSx}><Tooltip title={s.userAgent || ""}><span>{device(s.userAgent, s.clientType)}</span></Tooltip></TableCell>
                    <TableCell sx={cellSx}><Tooltip title={when(s.lastSeenAt)}><span>{ago(s.lastSeenAt)}</span></Tooltip></TableCell>
                    <TableCell sx={cellSx}>{when(s.createdAt)}</TableCell>
                    <TableCell sx={cellSx}>{s.ipAddress}</TableCell>
                    <TableCell sx={cellSx}>
                      {s.state === "OPEN" && <Chip size="small" color="success" label="Signed in" />}
                      {s.state === "EXPIRED" && <Chip size="small" label="Expired" />}
                      {s.state === "SIGNED_OUT" && (
                        <Tooltip title={`${s.revokeReason || ""}${s.revokedBy ? ` (by ${s.revokedBy})` : ""}`}>
                          <Chip size="small" variant="outlined" label="Signed out" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      {s.state === "OPEN" && (
                        <Button size="small" color="warning" startIcon={<SignOutIcon />} onClick={() => signOutSession(s)}>
                          Sign out
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!shownSessions.length && (
                  <TableRow><TableCell colSpan={8} sx={{ color: "text.secondary" }}>Nobody is signed in{filter || tenantFilter ? " that matches" : ""}.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 1 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Phone / email</TableCell>
                  <TableCell>Signed in</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {shownUsers.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell sx={cellSx}>
                      {u.username}
                      {u.platformAdmin && <Chip size="small" color="primary" variant="outlined" label="Platform admin" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell sx={cellSx}>{company(u) || u.tenantId}</TableCell>
                    <TableCell sx={cellSx}>{[u.phone, u.email].filter(Boolean).join(" · ")}</TableCell>
                    <TableCell sx={cellSx}>{u.openSessions}</TableCell>
                    <TableCell sx={cellSx}>{ago(u.lastSeenAt)}</TableCell>
                    <TableCell sx={cellSx}>
                      {u.disabled
                        ? <Tooltip title={`${u.disabledReason || ""}${u.disabledBy ? ` (by ${u.disabledBy}, ${when(u.disabledAt)})` : ""}`}><Chip size="small" color="error" label="Disabled" /></Tooltip>
                        : <Chip size="small" variant="outlined" label="Active" />}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      <Button size="small" color="warning" startIcon={<SignOutIcon />} onClick={() => signOutUser(u.username)}>
                        Sign out
                      </Button>
                      {u.username !== me.username && (
                        <Button size="small" color={u.disabled ? "success" : "error"} startIcon={u.disabled ? <EnableIcon /> : <BlockIcon />} onClick={() => toggleUser(u)}>
                          {u.disabled ? "Enable" : "Disable"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!shownUsers.length && (
                  <TableRow><TableCell colSpan={7} sx={{ color: "text.secondary" }}>No users match.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 2 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Tenant id</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Signed in</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {shownTenants.map((t) => (
                  <TableRow key={t.tenantId} hover>
                    <TableCell sx={cellSx}>{company(t) || "—"}</TableCell>
                    <TableCell sx={cellSx}>{t.tenantId}</TableCell>
                    <TableCell sx={cellSx}>{[t.phone, t.email].filter(Boolean).join(" · ")}</TableCell>
                    <TableCell sx={cellSx}><Button size="small" onClick={() => viewTenant(t.tenantId, 1)}>{t.users}</Button></TableCell>
                    <TableCell sx={cellSx}><Button size="small" onClick={() => viewTenant(t.tenantId, 0)}>{t.openSessions}</Button></TableCell>
                    <TableCell sx={cellSx}>{ago(t.lastSeenAt)}</TableCell>
                    <TableCell sx={cellSx}>
                      {t.status === "SUSPENDED"
                        ? <Tooltip title={`${t.reason || ""}${t.changedBy ? ` (by ${t.changedBy}, ${when(t.changedAt)})` : ""}`}><Chip size="small" color="error" label="Suspended" /></Tooltip>
                        : <Chip size="small" color="success" variant="outlined" label="Active" />}
                    </TableCell>
                    <TableCell align="right" sx={cellSx}>
                      <Button size="small" color="warning" startIcon={<SignOutIcon />} onClick={() => signOutTenant(t)}>
                        Sign out all
                      </Button>
                      {t.tenantId !== me.tenantId && (
                        <Button size="small" color={t.status === "SUSPENDED" ? "success" : "error"}
                          startIcon={t.status === "SUSPENDED" ? <ActivateIcon /> : <SuspendIcon />} onClick={() => toggleTenant(t)}>
                          {t.status === "SUSPENDED" ? "Activate" : "Suspend"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!shownTenants.length && (
                  <TableRow><TableCell colSpan={8} sx={{ color: "text.secondary" }}>No companies match.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 3 && (
          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
              Platform admins see every company here and can sign users out, disable users and suspend companies.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <TextField size="small" label="Username" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addAdmin()} />
              <Button variant="contained" onClick={addAdmin} disabled={!newAdmin.trim()}>Add admin</Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Username</TableCell><TableCell>Authenticator</TableCell><TableCell>Added by</TableCell><TableCell>Added</TableCell><TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {admins.map((a) => (
                  <TableRow key={a.username}>
                    <TableCell>{a.username}</TableCell>
                    <TableCell>
                      {a.mfaEnabled
                        ? <Chip size="small" color="success" variant="outlined" label="On" />
                        : <Chip size="small" variant="outlined" label="Not set up" />}
                    </TableCell>
                    <TableCell>{a.addedBy}</TableCell>
                    <TableCell>{when(a.addedAt)}</TableCell>
                    <TableCell align="right">
                      {a.username.toLowerCase() !== (me.username || "").toLowerCase() && (
                        <>
                          {a.mfaEnabled && (
                            <IconButton size="small" onClick={() => resetAuthenticator(a.username)} title="Reset authenticator"><KeyOffIcon fontSize="small" /></IconButton>
                          )}
                          <IconButton size="small" onClick={() => removeAdmin(a.username)} title="Remove"><RemoveIcon fontSize="small" /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 4 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Action</TableCell><TableCell>Target</TableCell><TableCell>Detail</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {audit.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell sx={cellSx}>{when(a.at)}</TableCell>
                    <TableCell sx={cellSx}>{a.actor}</TableCell>
                    <TableCell sx={cellSx}>{a.action.replace(/_/g, " ").toLowerCase()}</TableCell>
                    <TableCell sx={cellSx}>{a.target}</TableCell>
                    <TableCell>{a.detail}</TableCell>
                  </TableRow>
                ))}
                {!audit.length && (
                  <TableRow><TableCell colSpan={5} sx={{ color: "text.secondary" }}>Nothing yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: confirm?.needsReason ? 2 : 0 }}>{confirm?.text}</DialogContentText>
          {confirm?.input && (
            <TextField autoFocus fullWidth size="small" label={confirm.input.label} value={typed}
              multiline={!!confirm.input.multiline} minRows={confirm.input.multiline ? 3 : undefined}
              onChange={(e) => setTyped(e.target.value)} sx={{ mb: 2 }}
              inputProps={{ style: confirm.input.multiline ? { fontFamily: "monospace", fontSize: 12 } : undefined }} />
          )}
          {confirm?.needsReason && (
            <TextField autoFocus={!confirm?.input} fullWidth size="small" label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={confirm?.danger ? "error" : "primary"} onClick={runConfirmed}
            disabled={!!confirm?.input && (confirm.input.match ? typed.trim() !== confirm.input.match : !typed.trim())}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
